/**
 * Notfallstellen für den Notrufplan — die REGELN, ohne Netz und ohne DOM.
 *
 * Hier steht ausschließlich, WAS eine brauchbare Notfallstelle ist. Das Reden
 * mit OpenStreetMap steht in services/osmMaps.ts, das Reden mit der Datenbank
 * in services/notfallStellenDb.ts, der Ablauf in components/sige-plan/notrufAuto.ts.
 * Diese Trennung ist Absicht: die Bewertung ist der Teil, der falsch sein kann,
 * und nur so ist sie ohne jsdom testbar.
 *
 * ─── Warum „nächstes Krankenhaus" nicht reicht ─────────────────────────────
 * Der Betrieb meldete Kinderkrankenhäuser im Notrufplan. Die alte Fassung nahm
 * je Kategorie den geringsten Abstand — OSM kennt aber unter `amenity=hospital`
 * auch Kinderkliniken, Psychiatrien, Reha- und Tageskliniken, Zahn- und
 * Tierkliniken. Für einen verunfallten Bauarbeiter ist die nächste davon die
 * falsche Adresse, und im Notrufplan steht sie schwarz auf weiß.
 *
 * Deshalb entscheidet nicht mehr die Entfernung allein, sondern eine EIGNUNG:
 *   • ungeeignet     — fachfremd (Kinder, Psychiatrie, Reha, Zahn, Tier …).
 *                      Wird nie automatisch eingesetzt, steht aber weiter in
 *                      der Auswahlliste: die Sonderfälle des echten Lebens
 *                      („im Ort gibt es NUR die Fachklinik") sollen nicht
 *                      unsichtbar werden.
 *   • eingeschraenkt — Spezialhaus mit Notaufnahme-Wahrscheinlichkeit
 *                      (Herzzentrum, Fachklinik) oder nur mittelbar passend
 *                      (Krankenhaus mit Chirurgie als D-Arzt-Vorschlag).
 *   • geeignet       — allgemeines Krankenhaus; `emergency=yes` zieht vor.
 *
 * Innerhalb einer Klasse zählt die Entfernung, gewichtet: ein Haus mit
 * ausgewiesener Notaufnahme darf weiter weg liegen als eines ohne.
 */

export type NotfallKategorie = 'krankenhaus' | 'durchgangsarzt' | 'augenarzt' | 'apotheke';

export const KATEGORIEN: NotfallKategorie[] = ['krankenhaus', 'durchgangsarzt', 'augenarzt', 'apotheke'];

export const KATEGORIE_LABEL: Record<NotfallKategorie, string> = {
  krankenhaus: 'Krankenhaus',
  durchgangsarzt: 'Durchgangsarzt',
  augenarzt: 'Augenarzt',
  apotheke: 'Apotheke',
};

export type Eignung = 'geeignet' | 'eingeschraenkt' | 'ungeeignet';

export interface LatLng { lat: number; lng: number; }

export type OsmTyp = 'node' | 'way' | 'relation';

export interface StellenKandidat {
  kategorie: NotfallKategorie;
  name: string;
  adresse: string;
  telefon: string;
  lat: number;
  lng: number;
  /** Luftlinie zur Baustelle in Metern. */
  entfernung: number;
  /** 'db' = von Kollegen geprüft, 'osm' = frisch aus OpenStreetMap. */
  quelle: 'db' | 'osm';
  osmTyp?: OsmTyp;
  osmId?: number;
  /** Gesetzt, wenn der Eintrag aus der geprüften Datenbank stammt. */
  geprueftVon?: string;
  geprueftAm?: string;
  eignung: Eignung;
  /** Warum eingeschränkt/ungeeignet — steht so in der Oberfläche. */
  hinweis?: string;
  /** Sortierschlüssel: gewichtete Entfernung in Metern, kleiner ist besser. */
  rang: number;
}

/**
 * Suchradien für OpenStreetMap. Großzügig genug für den ländlichen Raum, aber
 * nicht so groß, dass die Overpass-Abfrage in der Großstadt ausufert.
 */
export const SUCHRADIUS: Record<NotfallKategorie, number> = {
  krankenhaus: 25000,
  durchgangsarzt: 25000,
  augenarzt: 25000,
  apotheke: 6000,
};

/**
 * Ab wann ein GEPRÜFTER Datenbankeintrag die Umkreissuche ersetzen darf.
 *
 * Enger als der Suchradius, und das ist der Kern der Beschleunigung: Wer eine
 * Baustelle drei Kilometer neben einer schon bestätigten Klinik hat, bekommt
 * die Angabe ohne einen einzigen Netzzugriff. Liegt der nächste geprüfte
 * Eintrag weiter weg, wird trotzdem gesucht — sonst schleppte ein Projekt die
 * Klinik der Nachbarstadt mit. Die Apotheke ist bewusst am strengsten: sie
 * steht dicht, und „nächste" heißt hier wirklich nächste.
 */
export const VERTRAUENSRADIUS: Record<NotfallKategorie, number> = {
  krankenhaus: 12000,
  durchgangsarzt: 12000,
  augenarzt: 10000,
  apotheke: 1500,
};

/** Ein Suchtreffer aus Overpass, auf das Nötige eingedampft. */
export interface OsmElement {
  typ: OsmTyp;
  id: number;
  lat: number;
  lng: number;
  tags: Record<string, string>;
}

// ── Textmuster ─────────────────────────────────────────────────────────────
// Geprüft wird gegen Name, Betreiber und Fachgebiet zusammen — OSM verteilt
// dieselbe Aussage je nach Erfasser auf `name`, `operator` oder
// `healthcare:speciality`.

/** Fachfremd für die Erstversorgung eines Bauunfalls. */
const UNGEEIGNET_MUSTER: { muster: RegExp; grund: string }[] = [
  { muster: /kinder|p(ä|ae)diatr|paediatr|jugendpsych|s(ä|ae)ugling/i, grund: 'Kinderklinik' },
  { muster: /psychiatr|psychosomat|psychotherap|nervenklinik|suchtklinik|suchtmedizin/i, grund: 'Psychiatrie' },
  { muster: /rehabilitation|reha-|rehaklinik|\breha\b|kurklinik|kurhaus|sanatorium/i, grund: 'Reha-/Kurklinik' },
  { muster: /tagesklinik|day.?clinic/i, grund: 'Tagesklinik' },
  { muster: /zahn|kieferorthop|dental/i, grund: 'Zahnklinik' },
  { muster: /tierklinik|tier(ä|ae)rzt|veterin(ä|ae)r|tiermedizin/i, grund: 'Tiermedizin' },
  { muster: /palliativ|hospiz/i, grund: 'Palliativ/Hospiz' },
  { muster: /dialyse/i, grund: 'Dialysezentrum' },
  { muster: /geburtshaus|entbindung|frauenklinik|gyn(ä|ae)kolog/i, grund: 'Geburtshilfe/Gynäkologie' },
  { muster: /sch(ö|oe)nheit|(ä|ae)sthetisch|plastische chirurgie/i, grund: 'Ästhetische Chirurgie' },
  { muster: /pflegeheim|altenheim|seniorenresidenz|geriatr/i, grund: 'Pflege/Geriatrie' },
  { muster: /blutspende|blutbank/i, grund: 'Blutspende' },
];

/** Spezialhaus — kann eine Notaufnahme haben, ist aber kein allgemeines Haus. */
const EINGESCHRAENKT_MUSTER: { muster: RegExp; grund: string }[] = [
  { muster: /herzzentrum|kardiolog/i, grund: 'Herzzentrum' },
  { muster: /lungenklinik|pneumolog|thoraxklinik/i, grund: 'Lungenfachklinik' },
  { muster: /onkolog|krebs|tumorzentrum/i, grund: 'Onkologie' },
  { muster: /hautklinik|dermatolog/i, grund: 'Hautklinik' },
  { muster: /augenklinik|augenzentrum|ophthalmolog/i, grund: 'Augenklinik' },
  { muster: /neurolog(ische)? (fach)?klinik|epilepsiezentrum/i, grund: 'Neurologische Fachklinik' },
  { muster: /privatklinik|belegklinik|praxisklinik/i, grund: 'Privat-/Praxisklinik' },
  { muster: /fachklinik|fachkrankenhaus/i, grund: 'Fachklinik' },
];

const AUGEN_MUSTER = /augen(arzt|(ä|ae)rzt|heilkunde|klinik|zentrum|praxis|medizin)|ophthalmolog/i;
const OPTIKER_MUSTER = /optik|brillen|kontaktlinsen|h(ö|oe)rger(ä|ae)t/i;
const DARZT_MUSTER = /durchgangsarzt|d-?arzt|d-?(ä|ae)rzt|unfallchirurg|unfallmedizin|berufsgenossenschaft|traumatolog/i;
const CHIRURG_MUSTER = /chirurg|orthop(ä|ae)d|traumatology|surgery|orthopaedics/i;
const NOTAUFNAHME_MUSTER = /notaufnahme|notfallambulanz|rettungsstelle/i;
const VERSAND_MUSTER = /versandapotheke|online.?apotheke/i;

/** Name, Betreiber und Fachgebiet als EIN Text — so greift jedes Muster überall. */
export const suchtext = (tags: Record<string, string> = {}): string =>
  [
    tags.name, tags['name:de'], tags.official_name, tags.short_name,
    tags.operator, tags['healthcare:speciality'], tags.speciality,
    tags.healthcare, tags.hospital,
  ].filter(Boolean).join(' ');

const trifft = (text: string, liste: { muster: RegExp; grund: string }[]): string | null => {
  for (const { muster, grund } of liste) if (muster.test(text)) return grund;
  return null;
};

/** Luftlinie in Metern (Haversine). */
export function entfernungMeter(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const la1 = a.lat * Math.PI / 180;
  const la2 = b.lat * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Stabiler Schlüssel einer Stelle — der Konfliktschlüssel der Datenbank.
 *
 * Bewusst im Client gebildet und als gewöhnliche Spalte gespeichert: ein
 * partieller Unique-Index taugt nicht als Arbiter für `upsert` (PostgREST kann
 * nur Spaltennamen übergeben, siehe MIGRATIONS.md Nr. 32). Ein OSM-Objekt wird
 * über Typ+Id identifiziert; von Hand erfasste Stellen über Name und Ort auf
 * vier Nachkommastellen (~11 m) — nah genug, dass dieselbe Adresse trifft,
 * grob genug, dass eine leicht verschobene Koordinate keine Dublette erzeugt.
 */
export const stellenSchluessel = (
  kategorie: NotfallKategorie,
  s: { osmTyp?: OsmTyp; osmId?: number | null; name?: string; lat: number; lng: number },
): string => {
  if (s.osmTyp && s.osmId != null) return `${kategorie}|${s.osmTyp}/${s.osmId}`;
  const name = (s.name || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const k = (v: number) => Math.trunc(v * 10000);
  return `${kategorie}|manuell/${name}@${k(s.lat)},${k(s.lng)}`;
};

/**
 * Welche Kategorien kann dieses OSM-Objekt bedienen?
 *
 * Ein Objekt darf mehrere bedienen: die Universitätsklinik ist Krankenhaus UND
 * — wenn sie Unfallchirurgie führt — ein D-Arzt-Vorschlag. Genau das macht die
 * EINE gemeinsame Abfrage möglich; getrennte Abfragen je Kategorie waren der
 * Grund für die lange Wartezeit.
 */
export const klassifiziere = (tags: Record<string, string> = {}): NotfallKategorie[] => {
  const amenity = tags.amenity || '';
  const healthcare = tags.healthcare || '';
  const text = suchtext(tags);
  const raus: NotfallKategorie[] = [];

  if (amenity === 'hospital' || healthcare === 'hospital') {
    raus.push('krankenhaus');
    raus.push('durchgangsarzt'); // Bewertung entscheidet, ob nur als Vorschlag
    if (AUGEN_MUSTER.test(text)) raus.push('augenarzt');
  }
  if (amenity === 'pharmacy' || healthcare === 'pharmacy') raus.push('apotheke');
  if (amenity === 'doctors' || amenity === 'clinic' || healthcare === 'doctor' || healthcare === 'centre') {
    if (AUGEN_MUSTER.test(text)) raus.push('augenarzt');
    if (DARZT_MUSTER.test(text) || CHIRURG_MUSTER.test(text)) raus.push('durchgangsarzt');
  }
  return raus;
};

interface Bewertung { eignung: Eignung; hinweis?: string; gewicht: number; zuschlag: number; }

const bewerteKrankenhaus = (tags: Record<string, string>): Bewertung => {
  const text = suchtext(tags);
  const ungeeignet = trifft(text, UNGEEIGNET_MUSTER);
  if (ungeeignet) return { eignung: 'ungeeignet', hinweis: ungeeignet, gewicht: 1, zuschlag: 0 };

  const notfall = tags.emergency === 'yes' || NOTAUFNAHME_MUSTER.test(text);
  const eingeschraenkt = trifft(text, EINGESCHRAENKT_MUSTER);
  if (eingeschraenkt) {
    return {
      eignung: 'eingeschraenkt',
      hinweis: notfall ? `${eingeschraenkt} (mit Notaufnahme)` : eingeschraenkt,
      gewicht: 1,
      zuschlag: notfall ? 3000 : 8000,
    };
  }
  if (notfall) return { eignung: 'geeignet', hinweis: 'Notaufnahme ausgewiesen', gewicht: 0.6, zuschlag: 0 };
  if (tags.emergency === 'no') {
    return { eignung: 'eingeschraenkt', hinweis: 'keine Notaufnahme (laut OSM)', gewicht: 1.6, zuschlag: 3000 };
  }
  return { eignung: 'geeignet', gewicht: 1, zuschlag: 0 };
};

const bewerteAugenarzt = (tags: Record<string, string>): Bewertung => {
  const text = suchtext(tags);
  if (OPTIKER_MUSTER.test(text) && !/arzt|(ä|ae)rzt|klinik|ophthalmolog/i.test(text)) {
    return { eignung: 'ungeeignet', hinweis: 'Optiker, kein Arzt', gewicht: 1, zuschlag: 0 };
  }
  // Die Augenklinik ist hier gerade der Volltreffer — das Muster, das sie beim
  // Krankenhaus zurückstuft, darf in dieser Kategorie nicht mitgelten.
  const ungeeignet = trifft(text, UNGEEIGNET_MUSTER);
  if (ungeeignet) return { eignung: 'ungeeignet', hinweis: ungeeignet, gewicht: 1, zuschlag: 0 };
  const perFach = /ophthalmolog/i.test(`${tags['healthcare:speciality'] || ''} ${tags.speciality || ''}`);
  if (perFach) return { eignung: 'geeignet', gewicht: 0.9, zuschlag: 0 };
  if (AUGEN_MUSTER.test(text)) return { eignung: 'geeignet', gewicht: 1, zuschlag: 0 };
  return { eignung: 'eingeschraenkt', hinweis: 'Fachgebiet unklar', gewicht: 1, zuschlag: 8000 };
};

const bewerteDurchgangsarzt = (tags: Record<string, string>): Bewertung => {
  const text = suchtext(tags);
  const ungeeignet = trifft(text, UNGEEIGNET_MUSTER);
  if (ungeeignet) return { eignung: 'ungeeignet', hinweis: ungeeignet, gewicht: 1, zuschlag: 0 };

  const istKrankenhaus = tags.amenity === 'hospital' || tags.healthcare === 'hospital';
  if (DARZT_MUSTER.test(text)) {
    return istKrankenhaus
      ? { eignung: 'geeignet', hinweis: 'Klinik mit Unfallchirurgie', gewicht: 1, zuschlag: 0 }
      : { eignung: 'geeignet', gewicht: 0.9, zuschlag: 0 };
  }
  // Reine Chirurgie/Orthopädie ist NICHT automatisch D-Arzt — die Zulassung
  // vergibt die Berufsgenossenschaft, aus OSM ist sie nicht ablesbar. Deshalb
  // nur ein Vorschlag mit klarem Prüfauftrag, nie eine stille Übernahme.
  if (CHIRURG_MUSTER.test(text)) {
    return { eignung: 'eingeschraenkt', hinweis: 'Chirurgie/Orthopädie — D-Arzt-Zulassung prüfen', gewicht: 1, zuschlag: 6000 };
  }
  return {
    eignung: 'eingeschraenkt',
    hinweis: istKrankenhaus ? 'Krankenhaus — D-Arzt-Zulassung prüfen' : 'Fachgebiet unklar — D-Arzt-Zulassung prüfen',
    gewicht: 1,
    zuschlag: istKrankenhaus ? 9000 : 14000,
  };
};

const bewerteApotheke = (tags: Record<string, string>): Bewertung => {
  const text = suchtext(tags);
  if (VERSAND_MUSTER.test(text)) return { eignung: 'ungeeignet', hinweis: 'Versandapotheke', gewicht: 1, zuschlag: 0 };
  if (/tierapotheke/i.test(text)) return { eignung: 'ungeeignet', hinweis: 'Tierapotheke', gewicht: 1, zuschlag: 0 };
  return { eignung: 'geeignet', gewicht: 1, zuschlag: 0 };
};

const BEWERTER: Record<NotfallKategorie, (tags: Record<string, string>) => Bewertung> = {
  krankenhaus: bewerteKrankenhaus,
  durchgangsarzt: bewerteDurchgangsarzt,
  augenarzt: bewerteAugenarzt,
  apotheke: bewerteApotheke,
};

const EIGNUNG_STUFE: Record<Eignung, number> = { geeignet: 0, eingeschraenkt: 1, ungeeignet: 2 };

/** Bewertet ein Objekt für EINE Kategorie und liefert den Sortierrang mit. */
export const bewerte = (
  kategorie: NotfallKategorie,
  tags: Record<string, string>,
  entfernung: number,
): { eignung: Eignung; hinweis?: string; rang: number } => {
  const b = BEWERTER[kategorie](tags);
  return { eignung: b.eignung, hinweis: b.hinweis, rang: entfernung * b.gewicht + b.zuschlag };
};

/**
 * Rangvorteil eines geprüften Eintrags: er darf 20 % weiter weg liegen und
 * gewinnt trotzdem.
 *
 * Bewusst ein Bonus und KEIN Vorrang. Ein absoluter Vorrang hätte die
 * bestätigte Apotheke aus dem Nachbarort vor die 500 m entfernte gesetzt —
 * „geprüft" heißt „die Angaben stimmen", nicht „das ist überall die nächste".
 */
export const GEPRUEFT_BONUS = 0.8;

/**
 * Reihenfolge der Auswahlliste: erst die Eignungsklasse, dann der Rang.
 *
 * Eine bestätigte Stelle kommt aus der Datenbank als `geeignet` an
 * (notfallStellenDb.ts): ein Mensch hat sie angesehen, und dieses Urteil steht
 * über jedem Textmuster — auch dann, wenn der Name „Kinder" enthält und es im
 * Einzelfall trotzdem das richtige Haus ist. Ihren Vorsprung trägt sie im Rang
 * (GEPRUEFT_BONUS), nicht in der Sortierung; bei gleichem Rang gibt sie den
 * Ausschlag.
 */
export const sortiereKandidaten = (liste: StellenKandidat[]): StellenKandidat[] =>
  [...liste].sort((a, b) => {
    const stufe = EIGNUNG_STUFE[a.eignung] - EIGNUNG_STUFE[b.eignung];
    if (stufe !== 0) return stufe;
    if (a.rang !== b.rang) return a.rang - b.rang;
    return (b.quelle === 'db' ? 1 : 0) - (a.quelle === 'db' ? 1 : 0);
  });

/** Dubletten (dasselbe OSM-Objekt aus DB und Suche) zusammenführen — DB gewinnt. */
export const entdoppele = (liste: StellenKandidat[]): StellenKandidat[] => {
  const gesehen = new Map<string, StellenKandidat>();
  for (const k of liste) {
    const key = stellenSchluessel(k.kategorie, k);
    const alt = gesehen.get(key);
    if (!alt || (alt.quelle === 'osm' && k.quelle === 'db')) gesehen.set(key, k);
  }
  return [...gesehen.values()];
};

/**
 * Meter je Breitengrad. Der Wert gilt streng genommen nur am Äquator; der
 * Meridianbogen ist etwas kürzer. Für ein Suchfenster reicht die Näherung.
 */
export const GRAD_LAT_METER = 111320;

/**
 * Sicherheitszuschlag auf jedes Koordinatenfenster.
 *
 * Ohne ihn schneidet das Fenster rund ein Promille zu früh ab (die Näherung
 * oben überschätzt die Meter je Grad) — bei 25 km sind das knapp 30 m, in denen
 * ein Krankenhaus verschwinden könnte, das gerade noch im Radius liegt. Zu viel
 * kostet nichts: das Fenster ist nur der Grobfilter, den runden Umkreis schneidet
 * anschließend `entfernungMeter` gegen `SUCHRADIUS`.
 */
export const BOX_ZUSCHLAG = 1.02;

/**
 * Umkreis als Bounding-Box — `sued,west,nord,ost`, wie Overpass sie erwartet.
 *
 * ─── Warum keine `around`-Klausel mehr ─────────────────────────────────────
 * `around:25000` war der Grund, warum die Suche gar nichts mehr fand. Gemessen
 * am 2026-08-21 gegen overpass-api.de, Kiel-Innenstadt:
 *
 *   nwr[amenity=doctors][name~"Augen",i](around:25000,…)  → Zeitablauf nach 85 s
 *   nwr[amenity=doctors][name~"Augen",i](bbox)            → 847 ms, 10 Treffer
 *
 * Die Box ist größer als der Kreis — an den Ecken kommt zu viel herein. Das
 * macht nichts: `zuKandidaten` misst anschließend die echte Luftlinie und wirft
 * weg, was jenseits von `SUCHRADIUS` liegt. Der Kreis entsteht also weiterhin,
 * nur eben hier statt auf dem Gemeinschaftsserver.
 */
export const boundingBox = (center: LatLng, radiusM: number): string => {
  const dLat = (radiusM * BOX_ZUSCHLAG) / GRAD_LAT_METER;
  const cos = Math.max(0.01, Math.cos(center.lat * Math.PI / 180));
  const dLng = (radiusM * BOX_ZUSCHLAG) / (GRAD_LAT_METER * cos);
  const f = (v: number) => v.toFixed(6);
  return `${f(center.lat - dLat)},${f(center.lng - dLng)},${f(center.lat + dLat)},${f(center.lng + dLng)}`;
};

const ARZT_ARTEN = ['doctors', 'clinic'];

/**
 * Grundabfrage: alle gesuchten Kategorien in EINER Runde.
 *
 * Vorher waren es bis zu sechs Abfragen nacheinander, jede mit 1,1 s
 * Zwangspause aus der Nutzungsrichtlinie — das war die Wartezeit, über die der
 * Betrieb geklagt hat. Die Treffer werden hinterher an den Tags erkannt
 * (`klassifiziere`), deshalb braucht es keine getrennten Ausgabemengen.
 *
 * Ärzte werden hier NUR über `healthcare:speciality` gesucht — das ist der
 * billige Teil (gemessen 1,7 s für alle vier Kategorien). Die Suche über den
 * Namen kostete in derselben Messung rund zehn Sekunden extra und steht
 * deshalb in `bauNamensAbfrage`, die nur bei Bedarf läuft.
 */
export const bauOverpassAbfrage = (center: LatLng, kategorien: NotfallKategorie[]): string => {
  const bb = (k: NotfallKategorie) => boundingBox(center, SUCHRADIUS[k]);
  const teile: string[] = [];
  const will = (k: NotfallKategorie) => kategorien.includes(k);

  if (will('krankenhaus') || will('durchgangsarzt')) {
    teile.push(`nwr[amenity=hospital](${bb(will('krankenhaus') ? 'krankenhaus' : 'durchgangsarzt')});`);
  }
  if (will('apotheke')) teile.push(`nwr[amenity=pharmacy](${bb('apotheke')});`);

  // Ein gemeinsamer Fachgebiets-Regex fuer beide Arztkategorien: eine
  // Alternative mehr im Regex kostet nichts, eine Abfrage mehr kostet Sekunden.
  const faecher: string[] = [];
  if (will('augenarzt')) faecher.push('ophthalmology');
  if (will('durchgangsarzt')) faecher.push('traumatology', 'orthopaedics', 'surgery');
  if (faecher.length) {
    const k = will('augenarzt') ? 'augenarzt' : 'durchgangsarzt';
    for (const art of ARZT_ARTEN) {
      teile.push(`nwr[amenity=${art}]["healthcare:speciality"~"${faecher.join('|')}",i](${bb(k)});`);
    }
  }

  return `[out:json][timeout:60];(${teile.join('')});out center tags;`;
};

/**
 * Nachschlag über den Namen — nur für Kategorien, die sonst leer blieben.
 *
 * `healthcare:speciality` ist in Deutschland lückenhaft gepflegt; manche
 * Augenarztpraxen und Durchgangsärzte stehen nur mit sprechendem Namen in OSM.
 * Diese Suche ist die teure (rund zehn Sekunden gegenüber 1,7 s für die
 * Grundabfrage), deshalb läuft sie nicht auf Verdacht mit, sondern erst wenn
 * die Grundabfrage die Kategorie nicht beantwortet hat. Scheitert sie, bleibt
 * die Kategorie leer und der Status sagt es — die übrigen Treffer stehen dann
 * längst.
 */
export const bauNamensAbfrage = (center: LatLng, kategorien: NotfallKategorie[]): string => {
  const namen: string[] = [];
  if (kategorien.includes('augenarzt')) namen.push('Augen');
  if (kategorien.includes('durchgangsarzt')) namen.push('Durchgangsarzt', 'D-Arzt', 'Unfallchirurg');
  if (!namen.length) return '';

  const k = kategorien.includes('augenarzt') ? 'augenarzt' : 'durchgangsarzt';
  const bb = boundingBox(center, SUCHRADIUS[k]);
  const teile = ARZT_ARTEN.map(art => `nwr[amenity=${art}][name~"${namen.join('|')}",i](${bb});`);
  return `[out:json][timeout:60];(${teile.join('')});out center tags;`;
};

/**
 * Ein Zeitablauf bei Overpass ist KEIN leeres Ergebnis.
 *
 * Genau daran ist die Suche gescheitert: der Server antwortet in dem Fall mit
 * HTTP 200, einer leeren `elements`-Liste und einem `remark`-Feld
 * (`"runtime error: Query timed out …"`). Wer nur den Statuscode ansieht, hält
 * das für „nichts gefunden" und meldet dem Nutzer eine Fehlanzeige, obwohl
 * niemand gesucht hat. Deshalb wird die Anmerkung hier ausgewertet und als
 * Fehler behandelt.
 */
export const pruefeOverpassAntwort = (data: any): void => {
  const remark: string = typeof data?.remark === 'string' ? data.remark : '';
  if (!remark) return;
  if (/timed out|timeout/i.test(remark)) {
    throw new Error('Die Umkreissuche hat zu lange gedauert (OpenStreetMap-Zeitablauf) — bitte erneut versuchen.');
  }
  if (/runtime error|error/i.test(remark)) {
    throw new Error(`Die Umkreissuche wurde abgewiesen: ${remark}`);
  }
};

/** Overpass-Antwort -> flache Elementliste. Unbrauchbares fällt still weg. */
export const leseOverpass = (data: any): OsmElement[] => {
  const elemente: any[] = Array.isArray(data?.elements) ? data.elements : [];
  const raus: OsmElement[] = [];
  for (const el of elemente) {
    const lat = el.lat ?? el.center?.lat;
    const lng = el.lon ?? el.center?.lon;
    if (typeof lat !== 'number' || typeof lng !== 'number') continue;
    const typ: OsmTyp = el.type === 'way' ? 'way' : el.type === 'relation' ? 'relation' : 'node';
    raus.push({ typ, id: Number(el.id), lat, lng, tags: el.tags || {} });
  }
  return raus;
};

/** Adresse aus den OSM-Adressfeldern. */
export const baueAdresse = (tags: Record<string, string> = {}): string => {
  const strasse = [tags['addr:street'], tags['addr:housenumber']].filter(Boolean).join(' ');
  const ort = [tags['addr:postcode'], tags['addr:city']].filter(Boolean).join(' ');
  return [strasse, ort].filter(Boolean).join(', ');
};

export type KandidatenNachKategorie = Record<NotfallKategorie, StellenKandidat[]>;

export const leereKandidaten = (): KandidatenNachKategorie =>
  ({ krankenhaus: [], durchgangsarzt: [], augenarzt: [], apotheke: [] });

/**
 * OSM-Elemente -> bewertete Kandidaten je Kategorie.
 *
 * `gesperrt` sind Schlüssel, die ein Mitarbeiter ausdrücklich verworfen hat
 * („das ist hier die falsche Adresse"). Sie tauchen gar nicht mehr auf — das
 * ist der Weg, auf dem ein Fehlgriff dauerhaft verschwindet, auch wenn kein
 * Textmuster ihn erkennt.
 */
export const zuKandidaten = (
  center: LatLng,
  elemente: OsmElement[],
  kategorien: NotfallKategorie[],
  gesperrt: Set<string> = new Set(),
): KandidatenNachKategorie => {
  const raus = leereKandidaten();
  for (const el of elemente) {
    const entfernung = entfernungMeter(center, el);
    for (const kategorie of klassifiziere(el.tags)) {
      if (!kategorien.includes(kategorie)) continue;
      if (entfernung > SUCHRADIUS[kategorie]) continue;
      const schluessel = stellenSchluessel(kategorie, { osmTyp: el.typ, osmId: el.id, lat: el.lat, lng: el.lng });
      if (gesperrt.has(schluessel)) continue;
      const { eignung, hinweis, rang } = bewerte(kategorie, el.tags, entfernung);
      raus[kategorie].push({
        kategorie,
        name: el.tags.name || el.tags['name:de'] || el.tags.operator || 'Unbenannt',
        adresse: baueAdresse(el.tags),
        telefon: el.tags.phone || el.tags['contact:phone'] || '',
        lat: el.lat,
        lng: el.lng,
        entfernung: Math.round(entfernung),
        quelle: 'osm',
        osmTyp: el.typ,
        osmId: el.id,
        eignung,
        hinweis,
        rang,
      });
    }
  }
  for (const k of KATEGORIEN) raus[k] = sortiereKandidaten(raus[k]);
  return raus;
};

/** Der Vorschlag, der ins Formular geschrieben wird — nie ein „ungeeignet". */
export const besterVorschlag = (liste: StellenKandidat[]): StellenKandidat | null =>
  liste.find(k => k.eignung !== 'ungeeignet') || null;

/**
 * Welche Kategorien muss OpenStreetMap noch beantworten?
 *
 * Beantwortet ist eine Kategorie nur durch einen GEPRÜFTEN Eintrag innerhalb
 * des Vertrauensradius — und nur, wenn er auch fachlich taugt. Sind alle vier
 * beantwortet, entfällt der Netzzugriff vollständig; das ist der Normalfall,
 * sobald ein Gebiet einmal bearbeitet wurde.
 */
export const offeneKategorien = (db: KandidatenNachKategorie): NotfallKategorie[] =>
  KATEGORIEN.filter(k => {
    const treffer = besterVorschlag(db[k]);
    return !treffer || treffer.entfernung > VERTRAUENSRADIUS[k];
  });
